/**
 * Assignments Redux Slice
 *
 * Manages state for assignment definitions and enrollments with assignment deadlines
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AssignmentDefinition, CourseEnrollment, Tag, AssignmentStatus } from '../../apis/api-client';

export interface AssignmentsState {
  definitions: AssignmentDefinition[]; // For "Assignment Setup" tab
  userAssignments: CourseEnrollment[]; // For "Assignments" tab (user_course_assignments view)
  tags: Tag[]; // Cache user tags for dropdowns
  loading: boolean;
  error: string | null;
  filters: {
    courseId: number | null;
    status: AssignmentStatus | null;
    searchQuery: string;
  };
}

const initialState: AssignmentsState = {
  definitions: [],
  userAssignments: [],
  tags: [],
  loading: false,
  error: null,
  filters: {
    courseId: null,
    status: null,
    searchQuery: '',
  },
};

export const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    // Assignment Definitions
    setDefinitions: (state, action: PayloadAction<AssignmentDefinition[]>) => {
      state.definitions = action.payload;
      state.loading = false;
    },
    addDefinition: (state, action: PayloadAction<AssignmentDefinition>) => {
      state.definitions.push(action.payload);
    },
    updateDefinition: (state, action: PayloadAction<AssignmentDefinition>) => {
      const index = state.definitions.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.definitions[index] = action.payload;
      }
    },
    removeDefinition: (state, action: PayloadAction<number>) => {
      state.definitions = state.definitions.filter((d) => d.id !== action.payload);
    },

    // User Assignments (for Assignments tab)
    setUserAssignments: (state, action: PayloadAction<CourseEnrollment[]>) => {
      state.userAssignments = action.payload;
      state.loading = false;
    },

    // Tags
    setTags: (state, action: PayloadAction<Tag[]>) => {
      state.tags = action.payload;
    },

    // Loading & Error
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Filters
    setFilters: (state, action: PayloadAction<Partial<AssignmentsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
});

export const {
  setDefinitions,
  addDefinition,
  updateDefinition,
  removeDefinition,
  setUserAssignments,
  setTags,
  setLoading,
  setError,
  setFilters,
  resetFilters,
} = assignmentsSlice.actions;

export default assignmentsSlice.reducer;
